import { lazy, Suspense } from 'react';
import { Route, Routes, useLocation, useParams } from 'react-router';

import { LoadingFallback } from '../components/LoadingFallback';

const AboutScreen = lazy(() => import('../pages/AboutScreen'));
const AdaptiveResultsScreen = lazy(() => import('../pages/AdaptiveResultsScreen'));
const QuestionPage = lazy(() => import('../pages/QuestionPage'));
const RetryTestScreen = lazy(() => import('../pages/RetryTestScreen'));
const AuthScreen = lazy(() => import('../pages/AuthScreen'));
const CEFRLevelForm = lazy(() => import('../pages/CEFRLevelForm'));
const ErrorScreen = lazy(() => import('../pages/ErrorScreen'));
const LanguagePage = lazy(() => import('../pages/LanguagePage'));
const UserInfo2Form = lazy(() => import('../pages/UserInfo2Form'));
const UserInfoForm = lazy(() => import('../pages/UserInfoForm'));

const PATTERNS = {
  INDEX: '/',
  CEFR: '/cefr/',
  CEFR_LEVEL: '/cefr/:level?',
  ABOUT: '/about/',
  RETRY: '/retry/',
  // No page index in the URL: the backend decides which page comes next.
  QUESTION: '/question/',
  // Reached from a translation question: change the native language during the vocab test.
  LANGUAGE: '/language/',
  FORM2: '/form2/',
  AUTH: '/auth/',
  RESULTS: '/results/',
} as const;

export function makeSharedRoutes(base: string) {
  return {
    TEST_ROOT: `${base}${PATTERNS.INDEX}`,
    TEST_FORM2: `${base}${PATTERNS.FORM2}`,
    TEST_CEFR: `${base}${PATTERNS.CEFR}`,
    TEST_CEFR_LEVEL: `${base}/cefr/level/`,
    TEST_ABOUT: `${base}${PATTERNS.ABOUT}`,
    TEST_AUTH: `${base}${PATTERNS.AUTH}`,
    TEST_RETRY: `${base}${PATTERNS.RETRY}`,
    TEST_QUESTION: `${base}${PATTERNS.QUESTION}`,
    TEST_LANGUAGE: `${base}${PATTERNS.LANGUAGE}`,
    TEST_RESULTS: `${base}${PATTERNS.RESULTS}`,
    ERROR: `${base}/error`,
  } as const;
}

function useMountBase(): string {
  const { pathname } = useLocation();
  const splat = useParams()['*'] ?? '';
  return pathname.slice(0, pathname.length - splat.length).replace(/\/$/, '');
}

export const AdaptiveOAuthFlowRoutes = () => {
  const base = useMountBase();
  const ROUTES = makeSharedRoutes(base);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path={PATTERNS.INDEX} element={<UserInfoForm nextPath={ROUTES.TEST_FORM2} />} />
        <Route path={PATTERNS.CEFR} element={<CEFRLevelForm nextPath={ROUTES.TEST_ABOUT} />} />
        <Route
          path={PATTERNS.CEFR_LEVEL}
          element={<CEFRLevelForm nextPath={ROUTES.TEST_ABOUT} />}
        />
        <Route path={PATTERNS.ABOUT} element={<AboutScreen nextPath={ROUTES.TEST_AUTH} />} />
        <Route path={PATTERNS.RETRY} element={<RetryTestScreen />} />
        <Route path={PATTERNS.QUESTION} element={<QuestionPage />} />
        <Route
          path={PATTERNS.LANGUAGE}
          element={<LanguagePage nextPath={ROUTES.TEST_QUESTION} />}
        />
        <Route path={PATTERNS.FORM2} element={<UserInfo2Form nextPath={ROUTES.TEST_CEFR} />} />
        <Route path={PATTERNS.AUTH} element={<AuthScreen nextPath={ROUTES.TEST_QUESTION} />} />
        <Route path={PATTERNS.RESULTS} element={<AdaptiveResultsScreen />} />
        <Route path="*" element={<ErrorScreen />} />
      </Routes>
    </Suspense>
  );
};
